/**
 * ReviewStatus - Indicates the manual admin review status of a Reddit mention
 *
 * SESSION_045: Separated from MatchStatus (algorithm assessment) to track manual review workflow
 * Maps to backend: MentionedMe.Domain.Entities.Reddit.RedditMention.ReviewStatus (string property)
 *
 * Workflow states:
 * - Pending: Awaiting admin review
 * - Approved: Admin confirmed the match is authentic
 * - Rejected: Admin rejected the match as false positive
 * - Flagged: Admin flagged for further investigation (optional)
 */
export type ReviewStatus = 'Pending' | 'Approved' | 'Rejected' | 'Flagged';

/**
 * Type guard to check if a string is a valid ReviewStatus
 */
export function isReviewStatus(value: string): value is ReviewStatus {
  return ['Pending', 'Approved', 'Rejected', 'Flagged'].includes(value);
}

/**
 * Parse a string to ReviewStatus with fallback
 */
export function parseReviewStatus(value: string | null | undefined): ReviewStatus {
  if (!value) {
    return 'Pending';
  }
  return isReviewStatus(value) ? value : 'Pending';
}

/**
 * Get display-friendly label for ReviewStatus
 */
export function getReviewStatusLabel(status: ReviewStatus): string {
  const labels: Record<ReviewStatus, string> = {
    Pending: 'Pending Review',
    Approved: 'Approved',
    Rejected: 'Rejected',
    Flagged: 'Flagged'
  };
  return labels[status];
}

/**
 * Get color for review status (for UI components)
 */
export function getReviewStatusColor(status: ReviewStatus): string {
  const colors: Record<ReviewStatus, string> = {
    Pending: 'orange',
    Approved: 'green',
    Rejected: 'gray',
    Flagged: 'yellow'
  };
  return colors[status];
}
